/**
 * Slack App-management + OAuth calls for the config-token auto-install funnel
 * (docs/designs/slack-install-smoothing.md §Tier B).
 *
 * Two calls, both against api.slack.com, both handling token material — they NEVER
 * log it (no request/response body dumps). Each returns a discriminated result so
 * the route maps Slack's error string to an HTTP status without exceptions on the
 * happy path; a transport failure surfaces as `error: 'unreachable'` (→ 502).
 *
 * Contracts confirmed against docs.slack.dev (apps.manifest.create, oauth.v2.access).
 */
import { WebAPIPlatformError, WebClient } from '@slack/web-api';
const SLACK_TIMEOUT_MS = 10_000;
function slackError(error) {
    return error instanceof WebAPIPlatformError ? error.data.error : 'unreachable';
}
/** Slack's official client owns transport, encoding, API errors, and response types. */
export function createSlackConfigApi(options = {}) {
    const client = () => new WebClient(undefined, {
        fetch: options.fetch ?? globalThis.fetch,
        timeout: SLACK_TIMEOUT_MS,
        retryConfig: { retries: 0 },
        rejectRateLimitedCalls: true
    });
    return {
        async createApp(configToken, manifest) {
            try {
                const body = await client().apps.manifest.create({
                    token: configToken,
                    manifest: manifest
                });
                const credentials = body.credentials ?? {};
                if (!body.app_id ||
                    !credentials.client_id ||
                    !credentials.client_secret ||
                    !credentials.signing_secret ||
                    !body.oauth_authorize_url) {
                    return { ok: false, error: 'malformed_response' };
                }
                return {
                    ok: true,
                    app: {
                        appId: body.app_id,
                        clientId: credentials.client_id,
                        clientSecret: credentials.client_secret,
                        signingSecret: credentials.signing_secret,
                        oauthAuthorizeUrl: body.oauth_authorize_url
                    }
                };
            }
            catch (error) {
                return { ok: false, error: slackError(error) };
            }
        },
        async exportApp(configToken, appId) {
            try {
                const body = await client().apps.manifest.export({ token: configToken, app_id: appId });
                if (body.manifest === null || typeof body.manifest !== 'object' || Array.isArray(body.manifest)) {
                    return { ok: false, error: 'malformed_response' };
                }
                return { ok: true, manifest: body.manifest };
            }
            catch (error) {
                return { ok: false, error: slackError(error) };
            }
        },
        async updateApp(configToken, appId, manifest) {
            try {
                const body = await client().apps.manifest.update({
                    token: configToken,
                    app_id: appId,
                    manifest: manifest
                });
                if (typeof body.permissions_updated !== 'boolean')
                    return { ok: false, error: 'malformed_response' };
                return { ok: true, permissionsUpdated: body.permissions_updated };
            }
            catch (error) {
                return { ok: false, error: slackError(error) };
            }
        },
        async exchangeOAuth({ clientId, clientSecret, code, redirectUri }) {
            try {
                const body = await client().oauth.v2.access({
                    client_id: clientId,
                    client_secret: clientSecret,
                    code,
                    redirect_uri: redirectUri
                });
                if (!body.access_token || !body.app_id)
                    return { ok: false, error: 'malformed_response' };
                return {
                    ok: true,
                    result: {
                        botToken: body.access_token,
                        appId: body.app_id,
                        teamId: body.team?.id ?? null,
                        teamName: body.team?.name ?? null,
                        botUserId: body.bot_user_id ?? null,
                        installerUserId: body.authed_user?.id ?? null
                    }
                };
            }
            catch (error) {
                return { ok: false, error: slackError(error) };
            }
        },
        async rotateConfigToken(refreshToken) {
            try {
                const body = await client().tooling.tokens.rotate({ refresh_token: refreshToken });
                if (!body.token || !body.refresh_token || !body.exp)
                    return { ok: false, error: 'malformed_response' };
                return {
                    ok: true,
                    rotated: {
                        accessToken: body.token,
                        refreshToken: body.refresh_token,
                        accessExpiresAt: new Date(body.exp * 1000)
                    }
                };
            }
            catch (error) {
                return { ok: false, error: slackError(error) };
            }
        }
    };
}
/** The live implementation (the container wires this; tests inject a stub). */
export const slackConfigApi = createSlackConfigApi();
//# sourceMappingURL=slack-config-api.js.map